import { ReactNode, useState } from 'react'
import { FaChevronRight } from 'react-icons/fa'
import styles from './styles.module.scss'

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
    v !== null && typeof v === 'object' && !Array.isArray(v)

const renderPrimitive = (value: unknown): ReactNode => {
    if (value === null) return <span className={styles.jtNull}>null</span>
    if (value === undefined) return <span className={styles.jtNull}>undefined</span>
    if (typeof value === 'string') {
        return <span className={styles.jtString}>&quot;{value}&quot;</span>
    }
    if (typeof value === 'number') return <span className={styles.jtNumber}>{value}</span>
    if (typeof value === 'boolean') {
        return <span className={styles.jtBool}>{String(value)}</span>
    }
    return <span>{String(value)}</span>
}

interface JsonNodeProps {
    label?: string
    value: unknown
    depth: number
    isLast: boolean
    defaultOpenDepth: number
}

const JsonNode = ({ label, value, depth, isLast, defaultOpenDepth }: JsonNodeProps) => {
    const [open, setOpen] = useState(depth < defaultOpenDepth)

    const isObj = isPlainObject(value)
    const isArr = Array.isArray(value)

    if (!isObj && !isArr) {
        return (
            <div
                className={styles.jtLine}
                style={{ paddingLeft: `${depth * 18 + 18}px` }}
            >
                {label !== undefined && (
                    <>
                        <span className={styles.jtKey}>&quot;{label}&quot;</span>
                        <span className={styles.jtPunct}>: </span>
                    </>
                )}
                {renderPrimitive(value)}
                {!isLast && <span className={styles.jtPunct}>,</span>}
            </div>
        )
    }

    const entries: Array<[string, unknown]> = isObj
        ? Object.entries(value as Record<string, unknown>)
        : (value as unknown[]).map((v, i) => [String(i), v])

    const openBracket = isArr ? '[' : '{'
    const closeBracket = isArr ? ']' : '}'

    const isEmpty = entries.length === 0

    return (
        <div className={styles.jtNode}>
            <div
                className={`${styles.jtLine} ${isEmpty ? '' : styles.jtClickable}`}
                style={{ paddingLeft: `${depth * 18}px` }}
                onClick={isEmpty ? undefined : () => setOpen((o) => !o)}
                role={isEmpty ? undefined : 'button'}
                tabIndex={isEmpty ? undefined : 0}
                onKeyDown={
                    isEmpty
                        ? undefined
                        : (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  setOpen((o) => !o)
                              }
                          }
                }
            >
                {!isEmpty && (
                    <FaChevronRight
                        className={`${styles.jtChevron} ${open ? styles.jtChevronOpen : ''}`}
                    />
                )}
                {isEmpty && <span className={styles.jtChevronPlaceholder} />}
                {label !== undefined && (
                    <>
                        <span className={styles.jtKey}>&quot;{label}&quot;</span>
                        <span className={styles.jtPunct}>: </span>
                    </>
                )}
                <span className={styles.jtBracket}>{openBracket}</span>
                {isEmpty ? (
                    <>
                        <span className={styles.jtBracket}>{closeBracket}</span>
                        {!isLast && <span className={styles.jtPunct}>,</span>}
                    </>
                ) : (
                    !open && (
                        <>
                            <span className={styles.jtSummary}>
                                {entries.length} {isArr ? 'items' : 'keys'}
                            </span>
                            <span className={styles.jtBracket}>{closeBracket}</span>
                            {!isLast && <span className={styles.jtPunct}>,</span>}
                        </>
                    )
                )}
            </div>
            {open && !isEmpty && (
                <>
                    {entries.map(([k, v], i) => (
                        <JsonNode
                            key={k}
                            label={isArr ? undefined : k}
                            value={v}
                            depth={depth + 1}
                            isLast={i === entries.length - 1}
                            defaultOpenDepth={defaultOpenDepth}
                        />
                    ))}
                    <div
                        className={styles.jtLine}
                        style={{ paddingLeft: `${depth * 18}px` }}
                    >
                        <span className={styles.jtChevronPlaceholder} />
                        <span className={styles.jtBracket}>{closeBracket}</span>
                        {!isLast && <span className={styles.jtPunct}>,</span>}
                    </div>
                </>
            )}
        </div>
    )
}

interface JsonTreeProps {
    data: unknown
    initialOpenDepth?: number
}

/**
 * Collapsible JSON tree viewer. Click a node (or hit Enter/Space) to expand
 * or collapse it. The "Expand all" / "Collapse all" buttons remount the tree
 * with a new default depth, which resets per-node state to that depth — so
 * the user can fully reset the layout instead of fighting with persisted
 * open/closed states deep in the structure.
 */
export const JsonTree = ({ data, initialOpenDepth = 2 }: JsonTreeProps) => {
    const [version, setVersion] = useState(0)
    const [openDepth, setOpenDepth] = useState(initialOpenDepth)

    const expandAll = () => {
        setOpenDepth(Number.POSITIVE_INFINITY)
        setVersion((v) => v + 1)
    }

    const collapseAll = () => {
        setOpenDepth(0)
        setVersion((v) => v + 1)
    }

    const reset = () => {
        setOpenDepth(initialOpenDepth)
        setVersion((v) => v + 1)
    }

    return (
        <div className={styles.jsonTree}>
            <div className={styles.jtControls}>
                <button type="button" className={styles.jtControlButton} onClick={expandAll}>
                    Expand all
                </button>
                <button type="button" className={styles.jtControlButton} onClick={collapseAll}>
                    Collapse all
                </button>
                <button type="button" className={styles.jtControlButton} onClick={reset}>
                    Reset
                </button>
            </div>
            <div className={styles.jtBody}>
                <JsonNode
                    key={version}
                    value={data}
                    depth={0}
                    isLast={true}
                    defaultOpenDepth={openDepth}
                />
            </div>
        </div>
    )
}
